// Движок звука на howler.js.
//
// Howler грузит атлас одним файлом и режет его на звуки по разметке из json. Форматов у файла
// четыре, а по сети уходит один: Howler спрашивает браузер, какой из них тот умеет, и берёт
// первый подходящий из списка. Поэтому список идёт в него целиком.
//
// Зацикленная дорожка живёт одна: музыка сменяется, а не наслаивается. Меняются дорожки
// внахлёст — уходящая гаснет, приходящая набирает силу, см. ADR-50. Мьют глушит весь звук
// разом и дорожку не трогает: снятый мьют застаёт музыку там, куда она к тому времени дошла.
//
// Ничего не обрывается на полуслове. Звук, укороченный в каталоге, гасит свой хвост сам:
// кусок вырезан из середины дорожки, и оборвись он резко — щёлкнуло бы. Звук, у которого
// картинка кончилась раньше, уводят за ручку, см. SoundVoice.
//
// Браузер держит звук закрытым, пока игрок не сделал жест, см. ADR-47. Загрузке это не мешает:
// файл качается и разбирается на приостановленном контексте, а пускает его resume.
//
// Незагруженный звук игру не роняет и не держит: без картинки играть нечего, а без звука
// играть можно. Поэтому сбой загрузки идёт в консоль, и игра продолжается молча.

import { Howl, Howler } from 'howler';

import { soundCut, soundSprite, soundUrls, type SoundAtlas, type SoundEngine, type SoundLoader, type SoundPick, type SoundSprite, type SoundSpriteSource, type SoundVoice } from './engine';

/**
 * За сколько гаснет хвост укороченного звука.
 *
 * Кусок вырезан из дорожки, и на его конце звук идёт полным ходом. Гудок паровоза тянет
 * там свою ноту, и десятой доли секунды ей мало: щелчка нет, а обрыв слышен всё равно.
 * Треть секунды — это спад, который ухо принимает за конец гудка, а не за его обрыв.
 *
 * Столько же тает и картинка, которая идёт со звуком, см. FADE_SECONDS в ui/smoke.ts:
 * пар и голос гаснут вместе.
 *
 * Кусок короче этого времени гаснет весь: спад начинается с первого мига.
 */
const TAIL_SECONDS: number = 0.35;

/** За сколько одна дорожка музыки сменяет другую. */
const SWAP_SECONDS: number = 0.8;

/**
 * Грузит json-атлас.
 *
 * Сбой не бросает: игра без звука идёт, а вот без экрана она не идёт вовсе.
 */
async function loadAtlas(url: string): Promise<SoundAtlas | null> {
  try {
    const response: Response = await fetch(url, { headers: { accept: 'application/json' } });
    if (!response.ok)
      throw new Error(`Атлас ${url} отдан с кодом ${response.status}`);

    return (await response.json()) as SoundAtlas;
  } catch (error: unknown) {
    console.warn(`Атлас звуков ${url} не загрузился`, error);
    return null;
  }
}

/** Звук браузера: атласы грузит Howler, он же режет их на звуки. */
export class HowlerEngine implements SoundEngine, SoundLoader {
  /** Имя звука и файл, в котором он лежит. Атласов два, поэтому файлов тоже два. */
  readonly #howls = new Map<string, Howl>();

  /** Длина каждого звука в секундах: по ней с ним идёт в ногу картинка. */
  readonly #seconds = new Map<string, number>();

  /** Звуки, укороченные в каталоге: у них гасится хвост. */
  readonly #cut = new Set<string>();

  /** Идущая зацикленная дорожка. Null — сейчас ничего не тянется. */
  #looping: SoundVoice | null = null;

  /**
   * Грузит атлас и ждёт, пока файл со звуком будет разобран.
   *
   * Ждать нужно: до конца разбора Howler звуков не играет, а игра к тому времени уже крутит
   * барабаны. Незагруженный атлас ожидание не держит — оно кончается и на сбое.
   */
  async load(source: SoundSpriteSource<string>): Promise<void> {
    const atlas: SoundAtlas | null = await loadAtlas(source.url);
    if (atlas === null)
      return;

    const sprite: SoundSprite = soundSprite(source, atlas);

    const howl = new Howl({
      src: soundUrls(source.url, atlas),
      sprite,
      volume: source.volume ?? 1,
    });

    for (const [name, span] of Object.entries(sprite)) {
      this.#howls.set(name, howl);
      this.#seconds.set(name, span[1] / 1000);
    }

    // Укороченный звук кончается там, где дорожка ещё звучит, поэтому его хвост гасится.
    // Целый звук кончается тишиной, и гасить в нём нечего.
    for (const [name, pick] of Object.entries<SoundPick>(source.sounds)) {
      if (soundCut(pick))
        this.#cut.add(name);
    }

    await new Promise<void>((resolve) => {
      howl.once('load', () => resolve());
      howl.once('loaderror', (_id: number, error: unknown) => {
        console.warn(`Звуки атласа ${source.url} не разобраны`, error);
        resolve();
      });
    });
  }

  play(name: string): SoundVoice | null {
    const howl: Howl | undefined = this.#howls.get(name);
    if (howl === undefined)
      return null;

    const voice: SoundVoice = this.#start(howl, name);

    // Укороченный звук гасит свой хвост сам: до конца куска он идёт полным ходом.
    if (this.#cut.has(name)) {
      const tail: number = Math.max(this.seconds(name) - TAIL_SECONDS, 0);
      setTimeout(() => voice.fadeOut(TAIL_SECONDS), tail * 1000);
    }

    return voice;
  }

  seconds(name: string): number {
    return this.#seconds.get(name) ?? 0;
  }

  /**
   * Ставит дорожку вместо той, что идёт сейчас.
   *
   * Дорожки меняются внахлёст: уходящая гаснет, приходящая набирает силу с нуля. Оборвись
   * музыка разом — смена читалась бы как сбой, а не как смена, см. ADR-50.
   */
  loop(name: string | null): void {
    this.#looping?.fadeOut(SWAP_SECONDS);
    this.#looping = null;

    if (name === null)
      return;

    const howl: Howl | undefined = this.#howls.get(name);
    if (howl === undefined)
      return;

    this.#looping = this.#start(howl, name, SWAP_SECONDS);
  }

  mute(muted: boolean): void {
    Howler.mute(muted);
  }

  /**
   * Пускает звук и отдаёт ручку, которой его уводят.
   *
   * Громкость у звука своя, а не общая на файл: пока одна дорожка гаснет, вторая набирает
   * силу, и обе живут в одном файле.
   *
   * @param rise За сколько звук набирает силу. Ноль — звук идёт с первого мига в полную.
   */
  #start(howl: Howl, name: string, rise: number = 0): SoundVoice {
    const full: number = howl.volume();
    const id: number = howl.play(name);

    if (rise > 0)
      howl.fade(0, full, rise * 1000, id);

    return {
      fadeOut: (seconds: number): void => {
        // Громкость берётся у самого звука, а не у файла: этот звук мог набирать её с нуля
        // или уже гаснуть, и увести его надо с той силы, на которой он идёт сейчас.
        const now: number = howl.volume(id) as number;

        howl.fade(now, 0, seconds * 1000, id);
        setTimeout(() => howl.stop(id), seconds * 1000);
      },
    };
  }

  /**
   * Пускает звук браузера.
   *
   * Контекст заведён вместе с первым атласом, то есть задолго до жеста игрока, и потому стоит
   * приостановленным. Звать эту строку надо сразу за жестом: браузер пускает звук только с него.
   */
  resume(): void {
    const context: AudioContext | undefined = Howler.ctx;

    if (context !== undefined && context.state === 'suspended')
      void context.resume();
  }
}
